import React from "react";

import { type ChartData, type ChartOptions, type TooltipItem } from "chart.js";
import moment from "moment";

import { useUser } from "@components/user-context";
import {
  type TimeSeries,
  formatting,
  type metrics,
} from "@north-beam/nb-common";

import { customTooltip, makeDatasets } from "./helpers";
import { LineWithLine } from "./line-with-line-chart";

interface AlertChartProps {
  format: metrics.MetricType;
  serieses: TimeSeriesWithColor[];
  height: number;
}

export interface TimeSeriesWithColor {
  series: TimeSeries;
  color: string;
}

export const AlertChart = ({ serieses, format, height }: AlertChartProps) => {
  const { user } = useUser();
  const datasets = serieses.flatMap((series) =>
    makeDatasets(series.series, series.color, {}),
  );

  const view: ChartData<any> = {
    datasets,
  };

  const options: ChartOptions = {
    animation: undefined,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: false,
        mode: "index",
        intersect: false,
        external: customTooltip,
        itemSort: (a: TooltipItem<"line">, b: TooltipItem<"line">) =>
          b.label.localeCompare(a.label),
        callbacks: {
          label: ({ raw }: { raw: any }) =>
            formatting.formatMetric.exact(parseFloat(raw.y), format, {
              code: user.currencies.currentCurrency,
            }),
          title: (vs: TooltipItem<"line">[]) => {
            return vs.map(({ label }) => {
              const labelMoment = moment(label);
              if (!labelMoment.isValid()) {
                return "N/A";
              }
              return labelMoment.format("ddd, MMM D");
            })[0];
          },
          beforeLabel: (v: any) => datasets[v.datasetIndex!].label,
        },
      },
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        position: "right",
        grid: {
          tickLength: 5,
        },
        ticks: {
          padding: 5,
          callback: (value) =>
            formatting.formatMetric.approximate(
              typeof value === "string" ? parseFloat(value) : value,
              format,
              {
                code: user.currencies.currentCurrency,
              },
            ),
        },
      },
      x: {
        grid: {
          drawOnChartArea: false,
          drawTicks: true,
          tickLength: 5,
        },
        ticks: {
          padding: 5,
          maxRotation: 0,
          autoSkip: true,
          autoSkipPadding: 75,
        },
        type: "timeseries",
        time: { unit: "day", parser: "YYYY-MM-DD" },
      },
    },
  };

  return <LineWithLine data={view} options={options} height={height} />;
};
